'use client';

import type { RefObject } from 'react';
import { useCallback, useMemo } from 'react';
import type { Key } from 'react-stately';
import { useDeferredMount } from 'hooks/useDeferredMount';
import { noop } from 'utils/noop';
import { useUpdateKeySize } from '../hooks/useKeyedSizes';
import { useScrollBoundary } from '../hooks/useScrollBoundary';
import { useScrollKeyIntoView } from '../hooks/useScrollKeyIntoView';
import { useScrollTo } from '../hooks/useScrollTo';
import { useScrollToFirstItem } from '../hooks/useScrollToFirstItem';
import { useSetFocusedKey } from '../hooks/useSetFocusedKey';
import { useSmoothScroll } from '../hooks/useSmoothScrollTo';
import { useStabilizeMutation } from '../hooks/useStabilizeMutation';
import { useVisibleRect } from '../hooks/useVisibleRect';
import { getVisibleNodes } from '../layouts/get-visible-nodes';
import { useStackLayout } from '../layouts/stack/useStackLayout';
import type { ListFilterState, ListProps } from './types';

/**
 * A hook that virtualizes a list of items, providing a collection
 * of items that are visible within the viewport, and a set of
 * utilities to scroll to specific items, update item sizes, and
 * more.
 */

export function useVirtualizedList<T extends object>(
  /** virtualization props */
  props: Omit<ListProps<T>, 'children'>,
  /** list state, as returned from useFilteredListState */
  state: ListFilterState<T>,
  /** ref of the scrollable element */
  ref: RefObject<HTMLElement | null>
) {
  const {
    defaultSize = 400,
    overscan: overscanProp = 1,
    estimatedItemHeight = 50,
    estimatedHeaderHeight = 40,
    sectionGap = 0,
    itemGap = 0,
    onBoundary = noop,
    paddingY = 0,
    paddingTop: paddingTopProp,
    paddingBottom: paddingBottomProp,
  } = props;

  const paddingTop = paddingTopProp ?? paddingY;
  const paddingBottom = paddingBottomProp ?? paddingY;

  const deferredMount = useDeferredMount();

  const overscan = deferredMount ? overscanProp : 0;

  /**
   * Determine visible rect, which is updated when
   * the scroll position changes or the container
   * resizes.
   */

  const { visibleRect, scrollCache } = useVisibleRect(ref, {
    defaultHeight: defaultSize,
  });

  /**
   * Track the dimensions of each item in the collection.
   */

  const { getSize, setSize } = useUpdateKeySize(
    ref,
    {
      estimatedItemHeight,
      estimatedHeaderHeight,
    },
    {
      state,
      scrollCache,
    }
  );

  /**
   * Generate a layout tree based upon the collection
   */

  const {
    totalSize,
    keyboardDelegate,
    dropTargetDelegate,
    layout,
    layoutInfo,
  } = useStackLayout({
    state,
    getSize,
    sectionGap,
    itemGap,
    visibleRect,
    paddingTop,
    paddingBottom,
  });

  /**
   * Handle loading more items when the user scrolls to the end
   * of the list.
   */

  useScrollBoundary(visibleRect.y, totalSize, visibleRect.height, {
    onBoundary,
  });

  /**
   * Get the visible nodes based upon the layout and the visible rect,
   * including overscan value.
   */

  const {
    visibleNodes: nodes,
    isFullyMeasured,
    visibleNodeSet,
  } = useMemo(() => {
    return getVisibleNodes({
      layout,
      visibleRect,
      // @ts-expect-error FIXME(strictNullChecks)
      persistedKeys: new Set([state.selectionManager.focusedKey]),
      layoutInfo,
      overscanFactor: overscan,
    });
  }, [
    layout,
    overscan,
    visibleRect,
    state.selectionManager.focusedKey,
    layoutInfo,
  ]);

  /**
   * Update the size of an item in the collection.
   * The component that calls this hook should run it on
   * useLayoutEffect (upon mount) and when the resize
   * observer fires.
   */

  const updateItemSize = useCallback(
    (key: Key, size: { width: number; height: number }) => {
      setSize(key, size, { layoutInfo });
    },
    [layoutInfo, setSize]
  );

  /**
   * Smoothly scroll to a specific key.
   */

  const smoothScrollTo = useSmoothScroll(
    ref,
    {
      layoutInfo,
      visibleRect,
      onScrollComplete: props.onScrollComplete,
      scrollPadding: props.scrollPadding,
    },
    state
  );

  /**
   * Scroll immediately to a specific key.
   */

  const scrollTo = useScrollTo(
    ref,
    {
      layoutInfo,
      visibleRect,
      totalSize,
      onScrollComplete: props.onScrollComplete,
      scrollPadding: props.scrollPadding,
    },
    state
  );

  /**
   * Update our focused key when the scrollToKey prop changes,
   * allowing declarative scrolling to a specific key.
   */

  // @ts-expect-error FIXME(strictNullChecks)
  useSetFocusedKey(props.scrollToKey, state);

  /**
   * When our focused key changes, scroll it into view.
   */

  useScrollKeyIntoView(
    // @ts-expect-error FIXME(strictNullChecks)
    state.selectionManager.focusedKey,
    scrollTo,
    smoothScrollTo,
    { getAutoScrollConfig: props.getAutoScrollConfig }
  );

  /**
   * When mutating our collection, if the element mounts or unmounts
   * above our visible rect, we want to correct the scroll
   * position so that our current view doesn't shift.
   */

  useStabilizeMutation(
    {
      layoutInfo,
      sectionGap,
      scrollCache,
    },
    state,
    ref
  );

  /**
   * When our loading state changes, scroll or select the first
   * item in the list.
   */

  useScrollToFirstItem(
    {
      disallowEmptySelection: Boolean(props.disallowEmptySelection),
      keyboardDelegate,
    },
    state,
    ref
  );

  return {
    listProps: {
      nodes,
      totalSize,
      updateItemSize,
      layoutInfo,
      keyboardDelegate,
      dropTargetDelegate,
      isFullyMeasured,
      visibleRect,
      visibleNodeSet,
    },
    scrollTo,
    smoothScrollTo,
  };
}
